import React, { forwardRef, useEffect, useState } from 'react';
import { FormControl, FormHelperText, InputLabel } from '@mui/material';
import AsyncSelect from 'react-select/async';
import { getUsers } from 'views/App/Portal/LexID/Users/_data_sources/useOrganizationUsers';
import { User } from 'views/App/Portal/LexID/Users/_types/user';
import { baseApiUrl, fetcher } from 'data_sources/constants';
import { ApiCollectionResponse } from 'types/api_response';

interface UserOption {
  value: string;
  label: string;
  user: User;
}

export type UserPickerProps = {
  allowBlank?: boolean;
  allowMultiple?: boolean;
  id: string;
  label?: string;
  value?: User[];
  onChange: (value: User[]) => void;
  organizationId: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string;
  size?: string;
};

const UserPicker = forwardRef(
  (
    {
      allowBlank = false,
      allowMultiple = false,
      id,
      label,
      value = [],
      onChange,
      organizationId,
      placeholder,
      disabled = false,
      required,
      error,
      helperText,
      size,
    }: UserPickerProps,
    ref
  ) => {
    const loadUserOptions = (inputValue: string) => {
      return getUsers(organizationId, { search: inputValue, size: 100 }).then((users) => {
        const userOpts = users.map((user: User) => ({
          value: user.id!,
          label: `${user.firstName} ${user.lastName}`,
          user,
        })) as UserOption[];
        return userOpts;
      });
    };

    return (
      <FormControl
        key={id}
        fullWidth
        error={!!error}
        sx={{ p: 0, paddingTop: !!label ? '1rem' : 0 }}
      >
        {!!label && <InputLabel required={required}>{label}</InputLabel>}
        <AsyncSelect
          name={id}
          defaultValue={(value || []).map((x) => ({ value: x.id, label: `${x.firstName} ${x.lastName}`, user: x }))}
          cacheOptions
          defaultOptions
          loadOptions={loadUserOptions}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, width: 300 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          menuPosition='fixed'
          menuPlacement='auto'
          isMulti={allowMultiple}
          isSearchable
          isClearable
          isDisabled={disabled}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              onChange(newValue.map((x) => x.user));
            } else {
              const newValueOpt = newValue as UserOption;
              onChange(!!newValueOpt ? [newValueOpt.user] : []);
            }
          }}
        />
        {error && <FormHelperText error>{error.message}</FormHelperText>}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

export default UserPicker;
